import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient.js';
import { useAuth } from './AuthContext.js';

const TeamPage = () => {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const fetchMembers = async () => {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('*')
      .order('display_name');

    if (!profiles) {
      setLoading(false);
      return;
    }

    // Fetch applicator links for all users
    const userIds = profiles.map(p => p.user_id);
    const { data: applicators } = await supabase
      .from('applicators')
      .select('id, name, user_id, home_base_location')
      .in('user_id', userIds);

    const applicatorMap = {};
    (applicators || []).forEach(a => {
      applicatorMap[a.user_id] = a;
    });

    setMembers(profiles.map(p => ({
      ...p,
      applicator: applicatorMap[p.user_id] || null,
    })));
    setLoading(false);
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleRoleChange = async (member, newRole) => {
    if (member.user_id === currentUser.id) return;
    setSaving(member.id);
    setError(null);
    const { error: err } = await supabase
      .from('user_profiles')
      .update({ role: newRole })
      .eq('id', member.id);
    if (err) { setError(`Failed to update role: ${err.message}`); setSaving(null); return; }
    await fetchMembers();
    setSaving(null);
  };

  const handleToggleApplicator = async (member) => {
    setSaving(member.id);
    setError(null);

    if (member.is_applicator) {
      // Turn OFF: unlink from applicators table + update profile in parallel
      const ops = [
        supabase.from('user_profiles').update({ is_applicator: false }).eq('id', member.id),
      ];
      if (member.applicator) {
        ops.push(supabase.from('applicators').update({ user_id: null }).eq('id', member.applicator.id));
      }
      const results = await Promise.all(ops);
      const failed = results.find(r => r.error);
      if (failed) { setError(`Failed to update: ${failed.error.message}`); setSaving(null); return; }
    } else {
      // Turn ON: find existing unlinked applicator or create one
      const { data: existing } = await supabase
        .from('applicators')
        .select('id')
        .eq('user_id', member.user_id)
        .maybeSingle();

      if (!existing) {
        const { error: insertErr } = await supabase
          .from('applicators')
          .insert([{ name: member.display_name || 'New Applicator', user_id: member.user_id }]);
        if (insertErr) { setError(`Failed to create applicator: ${insertErr.message}`); setSaving(null); return; }
      }
      const { error: updateErr } = await supabase
        .from('user_profiles')
        .update({ is_applicator: true })
        .eq('id', member.id);
      if (updateErr) { setError(`Failed to update profile: ${updateErr.message}`); setSaving(null); return; }
    }

    await fetchMembers();
    setSaving(null);
  };

  if (loading) {
    return <div className="text-gray-300">Loading team...</div>;
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-white mb-6">Team Management</h1>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-2 rounded-md mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {members.map(member => {
          const isSelf = member.user_id === currentUser.id;
          const isSaving = saving === member.id;

          return (
            <div
              key={member.id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">
                      {member.display_name || 'Unnamed'}
                    </span>
                    {isSelf && (
                      <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">you</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{member.user_id}</p>
                  {member.applicator && (
                    <p className="text-xs text-gray-400 mt-1">
                      Home base: {member.applicator.home_base_location || 'not set'}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  {/* Role selector */}
                  <div className="text-right">
                    <label className="block text-xs text-gray-400 mb-1">Role</label>
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member, e.target.value)}
                      disabled={isSelf || isSaving}
                      className={`px-2 py-1 bg-gray-700 border border-gray-500/40 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-600 ${
                        isSelf ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="admin">Admin</option>
                      <option value="applicator">Applicator</option>
                    </select>
                  </div>

                  {/* Applicator toggle */}
                  <div className="text-center">
                    <label className="block text-xs text-gray-400 mb-1">Applicator</label>
                    <button
                      onClick={() => handleToggleApplicator(member)}
                      disabled={isSaving}
                      className={`w-10 h-6 rounded-full transition-colors relative ${
                        member.is_applicator ? 'bg-green-700' : 'bg-gray-700'
                      } ${isSaving ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          member.is_applicator ? 'translate-x-4' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {members.length === 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
          <p className="text-gray-400">No team members found.</p>
          <p className="text-gray-400 text-sm mt-1">
            Create user accounts in the Supabase dashboard, then add their profiles here.
          </p>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-400">
        <p>Users are created in the Supabase Authentication dashboard.</p>
        <p className="mt-1">After creating a user there, add a row to the user_profiles table to grant portal access.</p>
      </div>
    </div>
  );
};

export default TeamPage;
